import React, { useEffect, useState, useRef } from "react";
import "./WealthSeriesPortfolioKeyAdjustments.scss";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { connect } from "react-redux";
import { portfolioSymbolSetter } from "../../../../appRedux/ducks/portfolioTicker";
import moment from "moment";
import { useParams } from "react-router-dom";
import { init_portfolio } from "../../../../appRedux/ducks/wealthPortfolio";
import BeautifulSkeleton from "../../../common/Skeletons/BeautifulSkeleton";
import FallbackTickerIcon from "../../../common/FallbackTickerIcon/FallbackTickerIcon";
import { useAuth } from "../../../../contexts/Auth";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import ReactMarkdown from "react-markdown";
import {
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
  Typography,
  Box,
  Dialog,
  DialogContent,
  Button,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";

const renderTickerRow = (
  tickerData,
  index,
  isBlurred,
  portfolioTicker,
  setTicker,
  showAiEntryPrice,
  openReasonModal,
  setSelectedTickerInfo,
  selectedDate,
  isLatestDate,
) => {
  const {
    ticker,
    oldPercent,
    newPercent,
    difference,
    aiEntryPrice,
    aiReason,
    sector = "",
  } = tickerData;

  // Shared handler for both row and button clicks
  const handleTickerAction = () => {
    if (isLatestDate) {
      // If latest date, set ticker and info
      setTicker(ticker);
      setSelectedTickerInfo({
        ticker,
        reason: aiReason,
        isBuy: parseFloat(newPercent) > parseFloat(oldPercent),
        date: selectedDate,
      });
    } else {
      // If historical date, just open reasoning modal
      if (aiReason) {
        openReasonModal(ticker, aiReason);
      }
    }
  };

  const handleViewReasoning = (e) => {
    e.stopPropagation();
    handleTickerAction();
  };

  const handleRowClick = () => {
    handleTickerAction();
  };

  return (
    <div
      onClick={handleRowClick}
      key={index}
      className={`ws-portfolio-key-adjustment__item ${ticker === portfolioTicker && isLatestDate ? "active" : ""}`}
    >
      <div
        className={`ws-portfolio-key-adjustment__ticker ${isBlurred ? "blurred" : ""}`}
      >
        <FallbackTickerIcon
          ticker={ticker}
          className="ws-portfolio-key-adjustment__ticker-logo"
          src={`https://assets.tradealgo.com/stock-logos/${ticker}.png`}
        />
        <div>{ticker}</div>
        {oldPercent === "0.00" && newPercent !== "0.00" && (
          <div className="ws-portfolio-new-adjustment">New</div>
        )}
      </div>
      {!showAiEntryPrice && (
        <div className="ws-portfolio-key-adjustment__sector">{sector}</div>
      )}
      <div className="ws-portfolio-key-adjustment__percent">
        <div className="ws-portfolio-key-adjustment__percent-prev">
          {oldPercent}%
        </div>
        <ArrowRightAltIcon />
        <div className="ws-portfolio-key-adjustment__percent-new">
          {newPercent}%
        </div>
        <div className={`ws-portfolio-key-adjustment__change nuetral`}>
          {difference.startsWith("-") ? "" : "+"}
          {difference}%
        </div>
      </div>
      {showAiEntryPrice && (
        <div className="ws-portfolio-key-adjustment__ai-price">
          {aiEntryPrice ? `$${aiEntryPrice.toFixed(2)}` : "N/A"}
        </div>
      )}
      <div
        className="ws-portfolio-key-adjustment__details"
        onClick={handleViewReasoning}
      >
        {isLatestDate ? "View Details" : "View Reasoning"}
        <ArrowOutwardIcon />
      </div>
    </div>
  );
};

const WealthSeriesPortfolioKeyAdjustments = ({
  setTicker,
  portfolioTicker,
  wealthPortfolios,
  loadingPortfolio,
  selectedTickerInfo,
  setSelectedTickerInfo,
  setIsViewingPastDate,
  portfolioData,
  isViewingPastDate,
}) => {
  const { isBlurred } = useAuth();
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAiEntryPrice, setShowAiEntryPrice] = useState(false);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const previousIsViewingPastDate = useRef(isViewingPastDate);
  const previousPortfolioId = useRef(null);

  const openReasonModal = (ticker, reason) => {
    setSelectedTickerInfo((prev) => ({
      ...prev,
      ticker,
      reason,
    }));
    setReasonModalOpen(true);
  };

  const closeReasonModal = () => {
    setReasonModalOpen(false);
  };

  const { portfolio } = useParams();
  // Use portfolioData from props if available, otherwise fall back to wealthPortfolios[portfolio]
  const currentPortfolio =
    portfolioData || wealthPortfolios[portfolio] || init_portfolio;

  const sortedAdjustments = (currentPortfolio?.adjustments || [])
    ?.map((adjustment) => ({
      ...adjustment,
      sortedTickers: Object.entries(adjustment.adjustments)
        ?.map(
          ([
            ticker,
            {
              old,
              new: newP,
              diff,
              ai_rebalance_price,
              ai_rebalance_reason,
              sector = "N/A",
            },
          ]) => ({
            ticker,
            oldPercent: (old * 100).toFixed(2),
            newPercent: (newP * 100).toFixed(2),
            difference: (diff * 100).toFixed(2),
            aiEntryPrice: ai_rebalance_price,
            aiReason: ai_rebalance_reason,
            sector: sector || "N/A",
          }),
        )
        ?.sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference)),
    }))
    ?.sort((a, b) => new Date(b.rebalance_date) - new Date(a.rebalance_date));

  const uniqueDates = React.useMemo(() => {
    const dates = sortedAdjustments.map((adj) => adj.rebalance_date);
    return [...new Set(dates)];
  }, [sortedAdjustments]);

  useEffect(() => {
    if (uniqueDates.length > 0) {
      // If we have a selectedTickerInfo date that exists in the new portfolio, use it
      if (
        selectedTickerInfo?.date &&
        uniqueDates.includes(selectedTickerInfo.date)
      ) {
        if (selectedDate !== selectedTickerInfo.date) {
          setSelectedDate(selectedTickerInfo.date);
        }
      } else if (!selectedDate || !uniqueDates.includes(selectedDate)) {
        // Only set to latest date if no date is selected or current date doesn't exist
        // This prevents overriding manual selections
        setSelectedDate(uniqueDates[0]);
      }
    }
  }, [uniqueDates, selectedTickerInfo]);

  // Reset selectedDate only when portfolio ID actually changes (handles portfolio switching)
  useEffect(() => {
    const currentPortfolioId = currentPortfolio?.portfolio_id;

    // Only reset if the portfolio ID has actually changed
    if (
      currentPortfolioId &&
      currentPortfolioId !== previousPortfolioId.current
    ) {
      if (uniqueDates.length > 0) {
        // When portfolio switches, reset to latest date unless we have a specific ticker date
        if (
          selectedTickerInfo?.date &&
          uniqueDates.includes(selectedTickerInfo.date)
        ) {
          setSelectedDate(selectedTickerInfo.date);
        } else {
          setSelectedDate(uniqueDates[0]);
        }
      } else {
        // If portfolio exists but no dates available, clear selected date
        setSelectedDate(null);
      }

      // Update the ref with the new portfolio ID
      previousPortfolioId.current = currentPortfolioId;
    } else if (!previousPortfolioId.current && currentPortfolioId) {
      // Handle initial load
      previousPortfolioId.current = currentPortfolioId;
    }
  }, [currentPortfolio?.portfolio_id, uniqueDates, selectedTickerInfo]);

  // Fix the effect to only reset when isViewingPastDate changes from true to false
  useEffect(() => {
    // Only reset if changing from true to false (clicking the breadcrumb)
    if (
      previousIsViewingPastDate.current === true &&
      isViewingPastDate === false
    ) {
      if (uniqueDates.length > 0 && selectedDate !== uniqueDates[0]) {
        setSelectedDate(uniqueDates[0]);
      }
    }

    // Update the ref value after the effect
    previousIsViewingPastDate.current = isViewingPastDate;
  }, [isViewingPastDate, uniqueDates]);

  const selectedAdjustmentData = React.useMemo(() => {
    return sortedAdjustments.find((adj) => adj.rebalance_date === selectedDate);
  }, [sortedAdjustments, selectedDate]);

  const isLatestDate = React.useMemo(() => {
    if (uniqueDates.length === 0 || !selectedDate) {
      return true;
    }

    return selectedDate === uniqueDates[0];
  }, [selectedDate, uniqueDates]);

  useEffect(() => {
    if (setIsViewingPastDate && !loadingPortfolio) {
      if (uniqueDates.length > 0 && selectedDate) {
        const isPastDate = !isLatestDate;
        setIsViewingPastDate(isPastDate);
      } else {
        setIsViewingPastDate(false);
      }
    }
  }, [
    isLatestDate,
    setIsViewingPastDate,
    loadingPortfolio,
    uniqueDates,
    selectedDate,
  ]);

  const { tickersToSell, tickersToBuy, tickersToHold } = React.useMemo(() => {
    const tickers = selectedAdjustmentData?.sortedTickers || [];

    // Split into the required categories
    const sell = tickers.filter((t) => parseFloat(t.difference) < 0);
    const buy = tickers.filter((t) => parseFloat(t.difference) > 0);
    const hold = tickers.filter((t) => parseFloat(t.difference) === 0);

    // Sort SELL by magnitude of change (largest to smallest)
    // And separate complete sells (final % = 0) from rebalanced down
    const completeSells = sell
      .filter((t) => parseFloat(t.newPercent) === 0)
      .sort(
        (a, b) =>
          Math.abs(parseFloat(b.difference)) -
          Math.abs(parseFloat(a.difference)),
      );

    const rebalancedDown = sell
      .filter((t) => parseFloat(t.newPercent) > 0)
      .sort(
        (a, b) =>
          Math.abs(parseFloat(b.difference)) -
          Math.abs(parseFloat(a.difference)),
      );

    // Sort BUY by magnitude of change (largest to smallest)
    // And separate new buys (initial % = 0) from rebalanced up
    const rebalancedUp = buy
      .filter((t) => parseFloat(t.oldPercent) > 0)
      .sort((a, b) => parseFloat(b.difference) - parseFloat(a.difference));

    const newBuys = buy
      .filter((t) => parseFloat(t.oldPercent) === 0)
      .sort((a, b) => parseFloat(b.difference) - parseFloat(a.difference));

    // Sort HOLD by descending portfolio weightage
    const sortedHold = hold.sort(
      (a, b) => parseFloat(b.newPercent) - parseFloat(a.newPercent),
    );

    return {
      tickersToSell: [...completeSells, ...rebalancedDown],
      tickersToBuy: [...rebalancedUp, ...newBuys],
      tickersToHold: sortedHold,
    };
  }, [selectedAdjustmentData]);
  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const handleSwitchChange = (event) => {
    setShowAiEntryPrice(event.target.checked);
  };

  // Should show "No adjustments" message only when:
  // - Not loading
  // - We have a selected date
  // - We have adjustments data available (at least empty arrays)
  // - All three arrays are actually empty
  const shouldShowNoAdjustments =
    !loadingPortfolio &&
    selectedAdjustmentData &&
    tickersToSell.length === 0 &&
    tickersToBuy.length === 0 &&
    tickersToHold.length === 0;

  if (loadingPortfolio) {
    return (
      <div className="ws-portfolio-key-adjustments">
        <div className="ws-portfolio-key-adjustments__header">
          Key Portfolio Adjustments
          <BeautifulSkeleton width={140} height={32} />
          <div className="ai-entry-price-switch-container">
            <BeautifulSkeleton width={180} height={32} />
          </div>
        </div>
        <Box sx={{ p: 2 }}>
          <BeautifulSkeleton variant="rectangular" height={60} />
        </Box>
        <div className="ws-portfolio-key-adjustment">
          <div className="ws-portfolio-key-adjustment__time">
            <BeautifulSkeleton width={170} height={20} />
          </div>
          <div className="w-full">
            {Array.from({ length: 5 })
              .fill(null)
              .map((_, index1) => (
                <div
                  key={index1}
                  className={`ws-portfolio-key-adjustment__item`}
                >
                  <div className="ws-portfolio-key-adjustment__ticker">
                    <BeautifulSkeleton
                      variant="circular"
                      width={24}
                      height={24}
                    />
                    <BeautifulSkeleton width={80} height={24} />
                  </div>
                  <div className="ws-portfolio-key-adjustment__sector">
                    <BeautifulSkeleton width={200} height={24} />
                  </div>
                  <div className="ws-portfolio-key-adjustment__percent">
                    <BeautifulSkeleton width={200} height={24} />
                  </div>
                  <div className={`ws-portfolio-key-adjustment__change`}>
                    <BeautifulSkeleton width={110} height={24} />
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ws-portfolio-key-adjustments">
      <div className="ws-portfolio-key-adjustments__header">
        Key Portfolio Adjustments
        {uniqueDates.length > 1 && (
          <select
            className="rebalance-date-select"
            value={selectedDate || ""}
            onChange={handleDateChange}
          >
            {uniqueDates.map((date) => (
              <option key={date} value={date}>
                {moment(date).format("MMM DD, YYYY")}
              </option>
            ))}
          </select>
        )}
        <div className="ai-entry-price-switch-container">
          <FormControlLabel
            control={
              <Switch
                checked={showAiEntryPrice}
                onChange={handleSwitchChange}
                size="small"
              />
            }
            label={
              <Typography variant="caption">Show AI Trade Price</Typography>
            }
            sx={{ mr: 0 }}
          />
          <Tooltip title="Displays the stock price used by the AI for this rebalance calculation.">
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {selectedAdjustmentData?.reason && (
        <div className="ws-portfolio-key-adjustment__reason-box">
          <AutoFixHighIcon className="ws-portfolio-key-adjustment__reason-icon" />
          <div className="ws-portfolio-key-adjustment__reason-content">
            <div className="ws-portfolio-key-adjustment__reason-text">
              <ReactMarkdown>{selectedAdjustmentData.reason}</ReactMarkdown>
            </div>
            <Button
              variant="outlined"
              disableRipple
              className="ws-portfolio-key-adjustment__read-more-button"
              onClick={() =>
                openReasonModal("Portfolio", selectedAdjustmentData.reason)
              }
            >
              Read more
            </Button>
          </div>
        </div>
      )}

      <div className="ws-portfolio-key-adjustments__content ws-scroll-element">
        <div className="ws-portfolio-key-adjustment__time">
          {moment(selectedDate).format("dddd, MMM DD, YYYY")}
        </div>

        <div className="ws-portfolio-key-adjustment__group">
          <div className="ws-portfolio-key-adjustment__group-header sell">
            {tickersToSell.length} TICKER
            {tickersToSell.length > 1 || tickersToSell.length === 0
              ? "S"
              : ""}{" "}
            TO ADJUST DOWN
          </div>
          <div className="w-full">
            {tickersToSell.map((tickerData, index1) =>
              renderTickerRow(
                tickerData,
                index1,
                isBlurred,
                portfolioTicker,
                setTicker,
                showAiEntryPrice,
                openReasonModal,
                setSelectedTickerInfo,
                selectedDate,
                isLatestDate,
              ),
            )}
          </div>
        </div>

        <div className="ws-portfolio-key-adjustment__group">
          <div className="ws-portfolio-key-adjustment__group-header buy">
            {tickersToBuy.length} TICKER
            {tickersToBuy.length > 1 || tickersToBuy.length === 0 ? "S" : ""} TO
            ADJUST UP
          </div>
          <div className="w-full">
            {tickersToBuy.map((tickerData, index1) =>
              renderTickerRow(
                tickerData,
                index1,
                isBlurred,
                portfolioTicker,
                setTicker,
                showAiEntryPrice,
                openReasonModal,
                setSelectedTickerInfo,
                selectedDate,
                isLatestDate,
              ),
            )}
          </div>
        </div>

        <div className="ws-portfolio-key-adjustment__group">
          <div className="ws-portfolio-key-adjustment__group-header neutral">
            {tickersToHold.length} TICKER
            {tickersToHold.length > 1 || tickersToHold.length === 0
              ? "S"
              : ""}{" "}
            TO HOLD
          </div>
          <div className="w-full">
            {tickersToHold.map((tickerData, index1) =>
              renderTickerRow(
                tickerData,
                index1,
                isBlurred,
                portfolioTicker,
                setTicker,
                showAiEntryPrice,
                openReasonModal,
                setSelectedTickerInfo,
                selectedDate,
                isLatestDate,
              ),
            )}
          </div>
        </div>
        {shouldShowNoAdjustments && (
          <div className="ws-portfolio-key-adjustment__no-items">
            No adjustments for this date.
          </div>
        )}
      </div>

      {/* AI Reasoning Modal */}
      <Dialog
        open={reasonModalOpen}
        onClose={closeReasonModal}
        maxWidth="sm"
        fullWidth
        className="ai-reasoning-modal"
        PaperProps={{
          style: { maxWidth: "720px" },
        }}
      >
        <div className="ai-reasoning-modal__header">
          <div className="ai-reasoning-modal__title">
            <span className="ai-reasoning-icon">
              <AutoFixHighIcon />
            </span>
            <Typography sx={{ whiteSpace: "nowrap" }} variant="h6">
              Adjustment Reasoning for {selectedTickerInfo.ticker}
            </Typography>
          </div>
          <Typography variant="caption" className="ai-reasoning-modal__date">
            {selectedDate && moment(selectedDate).format("MMM DD, YYYY")}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={closeReasonModal}
            className="ai-reasoning-modal__close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <DialogContent className="ai-reasoning-modal__content">
          {selectedTickerInfo.reason ? (
            <ReactMarkdown>{selectedTickerInfo.reason}</ReactMarkdown>
          ) : (
            <Typography variant="body1">
              No reasoning available for this adjustment.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const stateToProps = (state) => ({
  portfolioTicker: state.portfolioTicker.symbol,
  wealthPortfolios: state.wealthPortfolio,
  loadingPortfolio: state.wealthPortfolio.loading,
});

const dispatchToProps = (dispatch) => ({
  setTicker: (sym) => dispatch(portfolioSymbolSetter(sym)),
});

export default connect(
  stateToProps,
  dispatchToProps,
)(WealthSeriesPortfolioKeyAdjustments);
